import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { Box, Button, Flex, Popover, Text } from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonDualSlider } from 'components/common/form/dual-slider';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import useOnClickAway from 'hooks/useOnClickAway';
import { t } from 'i18next';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { INumericRangeFilter } from 'lib_ts/interfaces/common/i-num-range-filter';
import { ISHHitDetails } from 'lib_ts/interfaces/session-hits/i-hit-details';
import { IHitterSessionAdvancedFilters } from 'lib_ts/interfaces/session-hits/i-hitter-session-filters';
import { ISHPitchDetails } from 'lib_ts/interfaces/session-hits/i-pitch-details';
import { ISessionHit } from 'lib_ts/interfaces/session-hits/i-session-hit';
import { isNil } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';

const getMinMaxFilter = ({
  key,
  value,
  acc,
}: {
  key: keyof IHitterSessionAdvancedFilters;
  value?: number | null;
  acc: IHitterSessionAdvancedFilters;
}): [number, number] | null => {
  const { getMin, getMax } = MiscHelper;

  if (isNil(value)) {
    return null;
  }

  if (acc[key].minMax) {
    const existing = acc[key].minMax as [number, number];
    return [getMin([value, existing[0]]), getMax([value, existing[1]])];
  }

  return [value, value];
};

const getSpeedTransform = (isMetric?: boolean) =>
  isMetric ? MetricHelper.mphToKph : undefined;

const getDistanceTransform = (isMetric?: boolean) =>
  isMetric ? MetricHelper.ftToM : undefined;

const getBreaksTransform = (isMetric?: boolean) =>
  isMetric ? MetricHelper.inToCm : undefined;

const generateAdvancedFilters = (hits: ISessionHit[], isMetric?: boolean) =>
  hits
    .filter((h) => h.hit && h.pitch)
    .filter((h) => !h.hit?.invalid)
    .reduce(
      (acc, { hit, pitch }) => {
        const { exitMPH, vLaunchDEG, distanceFT } =
          hit as Partial<ISHHitDetails>;
        const { speedMPH, xBreakIN, zBreakIN } = pitch as ISHPitchDetails;

        const xBreakINfilter = getMinMaxFilter({
          key: 'xBreakIN',
          value: xBreakIN ? -1 * xBreakIN : undefined,
          acc,
        });
        acc.xBreakIN.minMax = xBreakINfilter;
        acc.xBreakIN.value = xBreakINfilter;

        const zBreakINfilter = getMinMaxFilter({
          key: 'zBreakIN',
          value: zBreakIN,
          acc,
        });
        acc.zBreakIN.minMax = zBreakINfilter;
        acc.zBreakIN.value = zBreakINfilter;

        const exitMPHfilter = getMinMaxFilter({
          key: 'exitMPH',
          value: exitMPH,
          acc,
        });
        acc.exitMPH.minMax = exitMPHfilter;
        acc.exitMPH.value = exitMPHfilter;

        const speedMPHfilter = getMinMaxFilter({
          key: 'speedMPH',
          value: speedMPH,
          acc,
        });
        acc.speedMPH.minMax = speedMPHfilter;
        acc.speedMPH.value = speedMPHfilter;

        const vLaunchDEGfilter = getMinMaxFilter({
          key: 'vLaunchDEG',
          value: vLaunchDEG,
          acc,
        });
        acc.vLaunchDEG.minMax = vLaunchDEGfilter;
        acc.vLaunchDEG.value = vLaunchDEGfilter;

        const distanceFTfilter = getMinMaxFilter({
          key: 'distanceFT',
          value: distanceFT,
          acc,
        });
        acc.distanceFT.minMax = distanceFTfilter;
        acc.distanceFT.value = distanceFTfilter;

        return acc;
      },
      {
        speedMPH: {
          label: isMetric
            ? t('common.pitch-speed-kph')
            : t('common.pitch-speed-mph'),
          minMax: null,
          value: null,
          step: 0.1,
          roundTo: 1,
          transform: getSpeedTransform(isMetric),
        },
        xBreakIN: {
          label: isMetric
            ? t('common.horizontal-break-cm')
            : t('common.horizontal-break-in'),
          minMax: null,
          value: null,
          step: 0.1,
          roundTo: 1,
          transform: getBreaksTransform(isMetric),
        },
        zBreakIN: {
          label: isMetric
            ? t('common.vertical-break-cm')
            : t('common.vertical-break-in'),
          minMax: null,
          value: null,
          step: 0.1,
          roundTo: 1,
          transform: getBreaksTransform(isMetric),
        },
        exitMPH: {
          label: isMetric
            ? t('common.exit-velocity-kph')
            : t('common.exit-velocity-mph'),
          minMax: null,
          value: null,
          step: 0.1,
          roundTo: 1,
          transform: getSpeedTransform(isMetric),
        },
        vLaunchDEG: {
          label: t('common.launch-angle-deg'),
          minMax: null,
          value: null,
          step: 0.1,
          roundTo: 1,
        },
        distanceFT: {
          label: isMetric ? t('common.distance-m') : t('common.distance-ft'),
          minMax: null,
          value: null,
          step: 1,
          roundTo: 0,
          transform: getDistanceTransform(isMetric),
        },
      } as IHitterSessionAdvancedFilters
    );

export const SessionHitsAdvancedFilters = () => {
  const [open, setOpen] = useState(false);

  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  const onClickAway = useCallback(() => {
    setOpen(false);
  }, []);

  useOnClickAway({
    ref: popoverRef,
    shouldListen: open,
    onClickAway,
    // don't handle clicks on the trigger element to prevent re-opening popover
    ignoreEls: [triggerRef.current],
  });

  const loading = useAnalyticsStore((s) => s.loadingSessions || s.loadingHits);

  return (
    <ErrorBoundary identifier="SessionHitsAdvancedFilters">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger>
          <Button
            className="btn-block"
            size={RADIX.BUTTON.SIZE.TABLE}
            color={RADIX.COLOR.NEUTRAL}
            variant={RADIX.BUTTON.VARIANT.SECONDARY}
            disabled={loading}
            ref={triggerRef}
          >
            <Flex gap="1" align="center">
              {t('common.advanced-filters')}
              {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Flex>
          </Button>
        </Popover.Trigger>
        <Popover.Content align="end" ref={popoverRef}>
          <Content onClose={() => setOpen(false)} />
        </Popover.Content>
      </Popover.Root>
    </ErrorBoundary>
  );
};

const Content = (props: { onClose: () => void }) => {
  const sessionMachine = useAnalyticsStore((s) => s.sessionMachine);
  const hits = useAnalyticsStore((s) => s.hits);
  const advancedFilters = useAnalyticsStore((s) => s.advancedFilters);

  const defaultInputs = useMemo(() => {
    if (advancedFilters) {
      return advancedFilters;
    }

    return generateAdvancedFilters(hits, sessionMachine?.isMetric);
  }, [advancedFilters, hits, sessionMachine?.isMetric]);

  const hasVisibleFilters = useMemo(() => {
    const visibleFilters = Object.keys(defaultInputs).reduce((acc, key) => {
      const filterKey = key as keyof IHitterSessionAdvancedFilters;
      const filter = defaultInputs[filterKey];

      const { minMax } = filter;

      // has distinct min and max values
      if (minMax && minMax[0] !== minMax[1]) {
        acc.push(filter);
      }

      return acc;
    }, [] as INumericRangeFilter[]);

    return visibleFilters.length > 0;
  }, [defaultInputs]);

  // Controlled inputs scoped to this component only to minimize unnecessary re-renders
  const [inputs, setInputs] =
    useState<IHitterSessionAdvancedFilters>(defaultInputs);

  return (
    <Flex
      p="4"
      direction="column"
      gap={RADIX.FLEX.GAP.MD}
      width={hasVisibleFilters ? '375px' : 'auto'}
    >
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        {!hasVisibleFilters && (
          <Box>
            <Text>{t('common.no-advanced-filters')}</Text>
          </Box>
        )}

        {Object.keys(inputs).map((key) => {
          const filter = inputs[key as keyof IHitterSessionAdvancedFilters];

          const { label, minMax, value, step, roundTo, transform } = filter;

          // Ignore if column has no data or only one value
          if (!minMax || minMax[0] === minMax[1]) {
            return null;
          }

          return (
            <CommonDualSlider
              id={key}
              key={key}
              label={label}
              defaultValue={minMax}
              min={minMax[0]}
              max={minMax[1]}
              step={step}
              minStepsBetweenThumbs={step}
              roundTo={roundTo}
              value={value as [number, number]}
              transform={transform}
              onValueChange={(value) =>
                setInputs((prev) => ({
                  ...prev,
                  [key]: {
                    ...prev[key as keyof IHitterSessionAdvancedFilters],
                    value,
                  },
                }))
              }
            />
          );
        })}
      </Flex>

      {hasVisibleFilters && (
        <Flex justify="end" gap={RADIX.FLEX.GAP.MD}>
          <Button
            variant="soft"
            size={RADIX.BUTTON.SIZE.SM}
            color={RADIX.COLOR.NEUTRAL}
            onClick={() => {
              setInputs(defaultInputs);
              useAnalyticsStore.setState({ advancedFilters: null });
              props.onClose();
            }}
          >
            {t('common.reset')}
          </Button>
          <Button
            variant="solid"
            size={RADIX.BUTTON.SIZE.SM}
            onClick={() => {
              useAnalyticsStore.setState({ advancedFilters: inputs });
              props.onClose();
            }}
          >
            {t('common.apply-filters')}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
